/**
 * Join Initiative Modal — allows a partner to join an open initiative with a budget commitment
 * @module components/initiatives/join-initiative-modal
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { joinInitiative } from '@/lib/api/initiatives';
import { t } from '@/lib/i18n/messages';
import { useToast } from '@/components/ui/toast';
import type { Locale } from '@/types/auth';
import type { Initiative } from '@/types/initiative';

interface JoinInitiativeModalProps {
  initiative: Initiative;
  locale: Locale;
  onClose: () => void;
  onSuccess: () => void;
}

interface FieldErrors {
  budget?: string[];
}

export function JoinInitiativeModal({
  initiative,
  locale,
  onClose,
  onSuccess,
}: JoinInitiativeModalProps) {
  const isRTL = locale === 'ar';
  const { toast } = useToast();

  const [budgetValue, setBudgetValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const modalRef = useRef<HTMLDivElement>(null);
  const firstFocusableRef = useRef<HTMLInputElement>(null);

  const getLocalizedTitle = () =>
    locale === 'ar'
      ? initiative.title_ar || initiative.title
      : initiative.title_en || initiative.title;

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }

      // Focus trap: Tab cycling within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusable = modalRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0] ?? null;
        const last = focusable[focusable.length - 1] ?? null;

        if (e.shiftKey) {
          if (document.activeElement === first && last) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last && first) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Auto-focus the budget input when modal opens
  useEffect(() => {
    firstFocusableRef.current?.focus();
  }, []);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const numericBudget = parseFloat(budgetValue) || 0;
  const isBudgetInvalid = numericBudget <= 0;

  const handleSubmit = async () => {
    if (isBudgetInvalid) return;

    setSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      const response = await joinInitiative(initiative.id, { budget: numericBudget });

      if (response.success) {
        toast(
          locale === 'ar' ? 'تم الانضمام بنجاح' : 'Successfully joined initiative',
          'success'
        );
        onSuccess();
        onClose();
      } else {
        if (response.errors) {
          setFieldErrors(response.errors as FieldErrors);
        } else {
          setError(response.message ?? t(locale, 'error'));
        }
      }
    } catch {
      setError(t(locale, 'error'));
    } finally {
      setSubmitting(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-modal-title"
      onClick={handleBackdropClick}
    >
      <div
        ref={modalRef}
        className="bg-card w-full max-w-[480px] rounded-[24px] shadow-2xl overflow-hidden"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="px-8 pt-8 pb-4 text-center">
          <h2
            id="join-modal-title"
            className="text-xl font-bold text-gray-900"
          >
            {t(locale, 'joinInitiative')}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{getLocalizedTitle()}</p>
        </div>
        <div className="border-t border-gray-100 dark:border-gray-800 mx-8" />

        <div className="p-8 space-y-6">
          {/* Budget Input */}
          <div className="space-y-2">
            <label
              htmlFor="join-budget"
              className={`block text-sm font-medium text-gray-700 dark:text-gray-300 ${isRTL ? 'text-right' : ''}`}
            >
              {t(locale, 'joinBudgetLabel')}
            </label>
            <input
              id="join-budget"
              ref={firstFocusableRef}
              type="number"
              min="0.01"
              step="0.01"
              value={budgetValue}
              onChange={(e) => setBudgetValue(e.target.value)}
              placeholder={t(locale, 'joinBudgetPlaceholder')}
              dir="ltr"
              className={`w-full h-[56px] border border-gray-300 dark:border-gray-700 rounded-xl bg-card text-gray-900 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all text-base px-4 ${isRTL ? 'text-right' : ''}`}
              aria-invalid={isBudgetInvalid && budgetValue !== ''}
              aria-describedby={
                fieldErrors.budget ? 'join-budget-error' : undefined
              }
            />
            {/* Inline 422 field errors */}
            {fieldErrors.budget && fieldErrors.budget.length > 0 && (
              <p
                id="join-budget-error"
                className={`text-xs text-red-500 ${isRTL ? 'text-right' : ''}`}
              >
                {fieldErrors.budget[0]}
              </p>
            )}
            {/* Client-side validation */}
            {budgetValue !== '' && isBudgetInvalid && !fieldErrors.budget && (
              <p className={`text-xs text-red-500 ${isRTL ? 'text-right' : ''}`}>
                {locale === 'ar'
                  ? 'يجب أن يكون المبلغ أكبر من صفر'
                  : 'Budget must be greater than 0'}
              </p>
            )}
          </div>

          {/* General error */}
          {error && (
            <p
              id="join-general-error"
              className="text-sm text-red-500 text-center"
            >
              {error}
            </p>
          )}

          {/* Action Buttons */}
          <div className={`flex gap-4 pt-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isBudgetInvalid || submitting}
              aria-disabled={isBudgetInvalid || submitting}
              className="flex-1 h-[48px] bg-primary hover:bg-amber-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm shadow-primary/20"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t(locale, 'loading')}
                </span>
              ) : (
                t(locale, 'joinInitiative')
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-[48px] bg-card border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
            >
              {t(locale, 'cancelAction')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
